import { useAuth } from '../store/useAuth'
import { SITE } from '../lib/brand'
import { trialLeft } from '../lib/trial'

/**
 * THE CONTRACTOR'S OWN SUBSCRIPTION. Not his customers' balances.
 *
 * Money changes hands on the website, where the card details already live, so
 * every button here is a door rather than a form. A second place to enter a
 * card is a second place to get one wrong.
 *
 * NOTHING ON THIS SCREEN IS CALCULATED. The state, the trial end and the date
 * a paid pass runs through all come off the access view. If the view has not
 * answered yet, this says so and offers to look again; it does not fill the
 * gap with today plus thirty days, which is the single most convincing wrong
 * thing a billing screen can say.
 */

const longDate = (iso: string) => {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
}

export default function Billing() {
  const access = useAuth((s) => s.access)
  const trialEndsAt = useAuth((s) => s.trialEndsAt)
  const paidThrough = useAuth((s) => s.paidThrough)
  const email = useAuth((s) => s.session?.user.email ?? '')
  const refreshAccess = useAuth((s) => s.refreshAccess)

  const left = trialLeft(trialEndsAt, Date.now())
  const trialEnd = trialEndsAt ? longDate(trialEndsAt) : null
  const paidEnd = paidThrough ? longDate(paidThrough) : null

  return (
    <>
      <div className="ws-sec-head">
        <h2>Your LightsOS plan</h2>
        <p>This is what you pay us. Anything a customer owes you lives on their job.</p>
      </div>

      <div className="ws-bill">
        <div className="ws-bill-card">
          <h3>Status</h3>
          {access === 'unknown' ? (
            <>
              <b>Checking</b>
              <p>We have not heard back about this account yet. Nothing is wrong with it.</p>
              <button className="btn" onClick={() => void refreshAccess()}>
                Look again
              </button>
            </>
          ) : access === 'paid' ? (
            <>
              <b>Active</b>
              <p>
                {paidEnd
                  ? `Your season pass runs through ${paidEnd}.`
                  : 'Your season pass is active. The site has the dates and the receipt.'}
              </p>
              <a className="btn" href={`${SITE}/account.html`}>
                Manage on the site &#8599;
              </a>
            </>
          ) : access === 'trial' ? (
            <>
              {/* The same count the strip at the top uses. See lib/trial.ts. */}
              <b>{left ? `${left.days} ${left.days === 1 ? 'day' : 'days'} left` : 'Trial'}</b>
              <p>
                {trialEnd
                  ? `Your free trial runs through ${trialEnd}. Everything works until then.`
                  : 'You are on the free trial. Everything works.'}
              </p>
              <a className="btn primary" href={`${SITE}/#pricing`}>
                Choose a plan &#8599;
              </a>
            </>
          ) : (
            <>
              <b>Ended</b>
              <p>
                Every job, customer and quote you have made still opens and still reads. You
                cannot start anything new until this is renewed.
              </p>
              <a className="btn primary" href={`${SITE}/#pricing`}>
                See plans &#8599;
              </a>
            </>
          )}
        </div>

        <div className="ws-bill-card">
          <h3>Account</h3>
          <b>{email || 'Signed in'}</b>
          <p>
            Receipts and payment details are kept on the site, on the same account you sign in
            with.
          </p>
          <a className="btn" href={`${SITE}/account.html`}>
            Open your account &#8599;
          </a>
        </div>
      </div>
    </>
  )
}
